import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { auth } from './firebase';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  sendPasswordResetEmail,
} from 'firebase/auth';
import './App.css';

/**
 * Handles all Firebase auth email action links:
 *   ?mode=resetPassword&oobCode=xxx   — set a new password
 *   ?mode=verifyEmail&oobCode=xxx     �� verify email address
 *   ?mode=recoverEmail&oobCode=xxx    — revert an email change
 *   ?mode=forgotPassword              — custom: show "send reset email" form
 */
export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  if (mode === 'forgotPassword') return <ForgotPasswordView />;
  if (mode === 'resetPassword' && oobCode) return <ResetPasswordView oobCode={oobCode} />;
  if (mode === 'verifyEmail' && oobCode) return <VerifyEmailView oobCode={oobCode} />;
  if (mode === 'recoverEmail' && oobCode) return <RecoverEmailView oobCode={oobCode} />;

  // Fallback — invalid or missing params
  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={containerStyle}>
        <h2 style={headingStyle}>Invalid Link</h2>
        <p style={subTextStyle}>
          This link is invalid or has expired. Please request a new one.
        </p>
        <Link to="/" style={linkStyle}>Go to mChatAI</Link>
      </section>
    </div>
  );
}

// ─── Forgot Password (custom mode — entry point for native apps) ──────────

function ForgotPasswordView() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    setError('');
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setStatus('sent');
    } catch (err) {
      // Firebase returns success even for unknown emails (enumeration protection)
      // Only real errors are network/config issues
      if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
        setStatus('error');
      } else {
        // Treat as success — don't leak whether the email exists
        setStatus('sent');
      }
    }
  };

  if (status === 'sent') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <div style={iconCircleStyle}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={headingStyle}>Check Your Email</h2>
          <p style={subTextStyle}>
            If an account exists for <strong style={{ color: '#f8fafc' }}>{email}</strong>,
            you'll receive a password reset link shortly. Check your spam folder if you don't see it.
          </p>
          <Link to="/" style={linkStyle}>Back to mChatAI</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={containerStyle}>
        <h2 style={headingStyle}>Reset Your Password</h2>
        <p style={subTextStyle}>
          Enter the email address associated with your mChatAI account.
        </p>
        <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 340 }}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
          />
          {error && <p style={errorStyle}>{error}</p>}
          <button type="submit" disabled={status === 'sending'} style={primaryBtnStyle}>
            {status === 'sending' ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        <Link to="/" style={{ ...linkStyle, marginTop: 24 }}>Back to mChatAI</Link>
      </section>
    </div>
  );
}

// ─── Reset Password (Firebase standard mode) ─────────────────────────────

function ResetPasswordView({ oobCode }) {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying | ready | resetting | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setStatus('ready');
      })
      .catch(() => {
        setError('This password reset link has expired or already been used.');
        setStatus('error');
      });
  }, [oobCode]);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setStatus('resetting');
    setError('');
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('done');
    } catch (err) {
      setError(err.code === 'auth/weak-password'
        ? 'Password must be at least 6 characters.'
        : 'Failed to reset password. The link may have expired.');
      setStatus('ready');
    }
  };

  if (status === 'verifying') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <p style={subTextStyle}>Verifying reset link...</p>
        </section>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <h2 style={headingStyle}>Link Expired</h2>
          <p style={subTextStyle}>{error}</p>
          <Link to="/auth/action?mode=forgotPassword" style={linkStyle}>Request a new reset link</Link>
        </section>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <div style={iconCircleStyle}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={headingStyle}>Password Reset Complete</h2>
          <p style={subTextStyle}>
            Your password has been updated. You can now sign in with your new password on any platform.
          </p>
          <Link to="/" style={linkStyle}>Sign in to mChatAI</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={containerStyle}>
        <h2 style={headingStyle}>Set New Password</h2>
        <p style={subTextStyle}>
          For <strong style={{ color: '#f8fafc' }}>{email}</strong>
        </p>
        <form onSubmit={handleReset} style={{ width: '100%', maxWidth: 340 }}>
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={6}
            style={{ ...inputStyle, marginTop: 10 }}
          />
          {error && <p style={errorStyle}>{error}</p>}
          <button type="submit" disabled={status === 'resetting'} style={primaryBtnStyle}>
            {status === 'resetting' ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </section>
    </div>
  );
}

// ─── Verify Email ────────────────────────────────────────────────────────

function VerifyEmailView({ oobCode }) {
  const [status, setStatus] = useState('verifying'); // verifying | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    applyActionCode(auth, oobCode)
      .then(() => setStatus('done'))
      .catch(() => {
        setError('This verification link has expired or already been used.');
        setStatus('error');
      });
  }, [oobCode]);

  if (status === 'verifying') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <p style={subTextStyle}>Verifying your email...</p>
        </section>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <h2 style={headingStyle}>Verification Failed</h2>
          <p style={subTextStyle}>{error}</p>
          <Link to="/" style={linkStyle}>Go to mChatAI</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={containerStyle}>
        <div style={iconCircleStyle}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 style={headingStyle}>Email Verified</h2>
        <p style={subTextStyle}>Your email has been verified. You're all set.</p>
        <Link to="/" style={linkStyle}>Go to mChatAI</Link>
      </section>
    </div>
  );
}

// ─── Recover Email (revert email change) ──────────────────────────────────

function RecoverEmailView({ oobCode }) {
  const [status, setStatus] = useState('recovering'); // recovering | done | error
  const [restoredEmail, setRestoredEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const recover = async () => {
      try {
        const info = await checkActionCode(auth, oobCode);
        setRestoredEmail(info.data.email || '');
        await applyActionCode(auth, oobCode);
        setStatus('done');
      } catch {
        setError('This recovery link has expired or already been used.');
        setStatus('error');
      }
    };
    recover();
  }, [oobCode]);

  if (status === 'recovering') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <p style={subTextStyle}>Recovering your email...</p>
        </section>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
        <section style={containerStyle}>
          <h2 style={headingStyle}>Recovery Failed</h2>
          <p style={subTextStyle}>{error}</p>
          <Link to="/" style={linkStyle}>Go to mChatAI</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={containerStyle}>
        <div style={iconCircleStyle}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 style={headingStyle}>Email Restored</h2>
        <p style={subTextStyle}>
          Your email has been reverted to <strong style={{ color: '#f8fafc' }}>{restoredEmail}</strong>.
          If you didn't make this change, consider resetting your password.
        </p>
        <Link to="/auth/action?mode=forgotPassword" style={linkStyle}>Reset password</Link>
      </section>
    </div>
  );
}

// ─── Shared styles ───────────────────────────────────────────────────────

const containerStyle = {
  maxWidth: 480,
  margin: '0 auto',
  padding: '100px 24px 48px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const headingStyle = {
  color: '#f8fafc',
  fontSize: '1.5rem',
  fontWeight: 700,
  margin: '0 0 12px',
};

const subTextStyle = {
  color: '#94a3b8',
  fontSize: '0.9rem',
  lineHeight: 1.6,
  margin: '0 0 24px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid #334155',
  background: '#0f172a',
  color: '#f8fafc',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtnStyle = {
  width: '100%',
  marginTop: 14,
  padding: '12px 24px',
  borderRadius: 8,
  border: 'none',
  background: '#3b82f6',
  color: '#fff',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const linkStyle = {
  color: '#3b82f6',
  fontSize: '0.85rem',
  textDecoration: 'none',
};

const errorStyle = {
  color: '#f87171',
  fontSize: '0.8rem',
  margin: '8px 0 0',
};

const iconCircleStyle = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: '#064e3b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
};
